import React from "react";
import { Link } from "react-router-dom";
import { Star, ShoppingCart, Heart, GitCompare } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { format, addToCart, t, inWishlist, toggleWishlist, inCompare, toggleCompare } = useApp();
  const hasSale = product.sale_price_eur && product.sale_price_eur < product.price_eur;
  const fav = inWishlist(product.id);
  const cmp = inCompare(product.id);

  const onWish = async (e) => {
    e.preventDefault();
    const added = await toggleWishlist(product.id);
    toast.success(added ? t("wishlist.added") : t("wishlist.removed"));
  };
  const onCompare = (e) => {
    e.preventDefault();
    const r = toggleCompare(product.id);
    if (r === null) toast.error("Máximo 4 produtos para comparar");
    else toast.success(r ? "Adicionado à comparação" : "Removido da comparação");
  };

  return (
    <div className="group relative rounded-2xl border border-border bg-card overflow-hidden card-hover hover:border-primary/40" data-testid={`product-card-${product.slug}`}>
      <button onClick={onWish} data-testid={`wish-${product.slug}`}
        className={`absolute top-3 right-3 z-10 p-2 rounded-full backdrop-blur transition-colors ${fav ? "bg-pink-500 text-white" : "bg-background/70 hover:bg-background text-foreground"}`}
        aria-label={fav ? t("wishlist.remove") : t("wishlist.add")}
      >
        <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
      </button>
      <button onClick={onCompare} data-testid={`compare-${product.slug}`}
        className={`absolute top-3 right-14 z-10 p-2 rounded-full backdrop-blur transition-colors ${cmp ? "bg-primary text-primary-foreground" : "bg-background/70 hover:bg-background text-foreground"}`}
        aria-label="Comparar"
        title="Comparar"
      >
        <GitCompare className="w-4 h-4" />
      </button>
      <Link to={`/produto/${product.slug}`} className="block">
        <div className="relative aspect-square overflow-hidden bg-secondary">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          {hasSale && (
            <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-bold px-2.5 py-1 rounded-full">
              -{Math.round(((product.price_eur - product.sale_price_eur) / product.price_eur) * 100)}%
            </span>
          )}
          {product.stock < 10 && product.stock > 0 && (
            <span className="absolute bottom-3 left-3 bg-yellow-500/90 text-black text-[10px] font-semibold px-2 py-0.5 rounded-full">
              {t("product.lastUnits")}
            </span>
          )}
          {product.stock === 0 && (
            <span className="absolute bottom-3 left-3 bg-destructive text-destructive-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full">
              {t("product.outOfStock")}
            </span>
          )}
        </div>
      </Link>
      <div className="p-5 space-y-2">
        <Link to={`/produto/${product.slug}`} className="block">
          <h3 className="font-display font-semibold text-base leading-snug line-clamp-1">{product.name}</h3>
        </Link>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
          <span className="font-medium text-foreground">{product.rating}</span>
          <span>({product.reviews_count})</span>
        </div>
        <div className="flex items-baseline gap-2 pt-1">
          <span className="font-display font-bold text-lg" data-testid={`price-${product.slug}`}>
            {format(product.sale_price_eur ?? product.price_eur)}
          </span>
          {hasSale && <span className="text-xs line-through text-muted-foreground">{format(product.price_eur)}</span>}
        </div>
        <Button size="sm" className="w-full mt-2 rounded-full btn-primary"
          onClick={(e) => { e.preventDefault(); addToCart(product); toast.success(`${product.name} — ${t("toast.addedCart")}`); }}
          disabled={product.stock === 0} data-testid={`add-to-cart-${product.slug}`}
        >
          <ShoppingCart className="w-4 h-4 mr-1.5" /> {t("cta.addToCart")}
        </Button>
      </div>
    </div>
  );
}
